import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { API_URL } from '@/lib/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Try with cookie first
      const headers = {};

      // Fallback: use localStorage token if cookie doesn't work
      const savedToken = localStorage.getItem('sabrina_session');
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers,
      });
      setUser(response.data);
    } catch {
      setUser(null);
      // Clear invalid saved token
      localStorage.removeItem('sabrina_session');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('sabrina_session');
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers,
      });
    } catch {
      // Ignore logout errors
    }
    setUser(null);
    localStorage.removeItem('sabrina_session');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
